import hexRgb from "hex-rgb";
import humanizeDuration from "humanize-duration";
import moment from "moment";
import numbro from "numbro";
import tzLookup from "tz-lookup";
import constants from "../constants";
import { baseURL } from "../api";

export const capitalize = (s = "", split = false) => {
  let string = s;
  if (!string) {
    return "";
  }
  if (split) {
    string = s.split(/(?=[A-Z])/).join(" ");
  }
  return `${string[0].toUpperCase()}${string.slice(1).toLowerCase()}`;
};

export const getColorFormat = (color, alpha = 0.2) => {
  const [r, g, b, a] = hexRgb(color, { alpha, format: "array" });
  return `rgba(${r},${g},${b},${a})`;
};

export function formatLocationResult(result, name = "", location = {}) {
  const _location = {
    ...location,
    placeId: result.place_id || null,
    country: "",
    state: "",
    city: "",
    postalCode: "",
    address: name.split(",").slice(0, -3).join(","),
    name,
    latitude: result.geometry.location.lat(),
    longitude: result.geometry.location.lng(),
  };
  for (let i = 0; i < result.address_components.length; i++) {
    for (let x = 0; x < result.address_components[i].types.length; x++) {
      const addressType = result.address_components[i].types[x];
      switch (addressType) {
        case "country":
          _location.country = result.address_components[i].long_name;
          break;
        case "administrative_area_level_1":
          _location.state = result.address_components[i].short_name;
          break;
        case "locality":
        case "postal_town":
          _location.city = result.address_components[i].long_name;
          break;
        case "postal_code":
          _location.postalCode = result.address_components[i].long_name;
          break;
      }
    }
  }
  if (!_location.city) {
    _location.city = _location.state;
  }
  _location.timezone = tzLookup(_location.latitude, _location.longitude);
  return _location;
}

export const formatDuration = (duration, split = true, largest = 2) => {
  return humanizeDuration(duration * 1000, {
    language: "es",
    units: ["d", "h", "m"],
    round: true,
    largest,
    delimiter: split ? ", " : " ",
  })
    .replace(/días|día/, "d")
    .replace(/horas|hora/, "h")
    .replace(/minutos|minuto/, "m")
    .replace(", ", "\r\n");
};

export function getBoardingPoints(
  origin = constants.location,
  boardingPoints = [constants.location],
) {
  const _boardingPoints = [];
  for (let index = 0; index < boardingPoints.length; index++) {
    const boardingPoint = { ...boardingPoints.at(index) };
    let previousLocation = origin;
    if (index > 0) {
      previousLocation = _boardingPoints.at(index - 1);
    }
    if (previousLocation && boardingPoint) {
      let duration = 0;
      if (boardingPoint.arrivalDate && boardingPoint.departureDate) {
        duration = moment(boardingPoint.departureDate).diff(
          moment(boardingPoint.arrivalDate),
          "minutes",
        );
      }
      if (origin.departureDate) {
        boardingPoint.arrivalDate = moment(previousLocation.departureDate)
          .add(previousLocation.duration, "seconds")
          .toISOString();
        boardingPoint.departureDate = moment(boardingPoint.arrivalDate)
          .add(duration, "minutes")
          .toISOString();
      }
      _boardingPoints.push(boardingPoint);
    }
  }
  return _boardingPoints;
}

export function getIntermediateDestinations(
  origin = constants.location,
  boardingPoints = [constants.location],
  intermediateDestinations = [constants.location],
) {
  const _intermediateDestinations = [];
  for (let index = 0; index < intermediateDestinations.length; index++) {
    const intermediateDestination = { ...intermediateDestinations.at(index) };
    let previousLocation = origin;
    if (index === 0) {
      if (boardingPoints.length > 0) {
        previousLocation = [...boardingPoints].pop();
      }
    } else {
      previousLocation = _intermediateDestinations.at(index - 1);
    }
    if (previousLocation && intermediateDestination) {
      let duration = 0;
      if (
        intermediateDestination.arrivalDate &&
        intermediateDestination.departureDate
      ) {
        duration = moment(intermediateDestination.departureDate).diff(
          moment(intermediateDestination.arrivalDate),
          "minutes",
        );
      }
      if (origin.departureDate) {
        intermediateDestination.arrivalDate = moment(
          previousLocation.departureDate,
        )
          .add(previousLocation.duration, "seconds")
          .toISOString();
        intermediateDestination.departureDate = moment(
          intermediateDestination.arrivalDate,
        )
          .add(duration, "minutes")
          .toISOString();
      }
      _intermediateDestinations.push(intermediateDestination);
    }
  }
  return _intermediateDestinations;
}

export function getDestination(
  origin = constants.location,
  boardingPoints = [constants.location],
  intermediateDestinations = [constants.location],
  destination = constants.location,
) {
  const _destination = { ...destination };
  let previousLocation = origin;
  if (intermediateDestinations.length > 0) {
    previousLocation = [...intermediateDestinations].pop();
  } else if (boardingPoints.length > 0) {
    previousLocation = [...boardingPoints].pop();
  }
  if (previousLocation) {
    let duration = 0;
    if (_destination.arrivalDate && _destination.departureDate) {
      duration = moment(_destination.departureDate).diff(
        moment(_destination.arrivalDate),
        "minutes",
      );
    }
    if (origin.departureDate) {
      _destination.arrivalDate = moment(previousLocation.departureDate)
        .add(previousLocation.duration, "seconds")
        .toISOString();
      _destination.departureDate = moment(_destination.arrivalDate)
        .add(duration, "minutes")
        .toISOString();
    }
  }
  return _destination;
}

export const formatToCurrency = (number = 0, currency = true) =>
  numbro(Math.ceil(number))
    .format({
      thousandSeparated: true,
      prefix: "$",
    })
    .concat(currency ? "MXN" : "");

export const getOriginalPrice = (price = 0, coupon = {}) => {
  let _price = price;
  if (coupon) {
    if (coupon.type === "percentage") {
      _price = price / ((100 - coupon.value) / 100);
    } else if (coupon.type === "amount") {
      _price = price + coupon.value;
    }
  }
  return Math.ceil(_price);
};

export const formatCouponValue = (coupon = {}) => {
  if (coupon) {
    if (coupon.type === "percentage") {
      return `${coupon.value}%`;
    } else if (coupon.type === "amount") {
      return formatToCurrency(coupon.value);
    }
  }
  return "";
};

export function getStatus(status) {
  switch (status) {
    case "approved":
      return "APROBADO";
    case "denied":
      return "DENEGADO";
    default:
      return "PENDIENTE";
  }
}

export function getStatusColor(status) {
  switch (status) {
    case "pending":
      return "orange.500";
    case "denied":
      return "red.600";
    default:
      return "green.600";
  }
}

export function preventExit(message = "") {
  window.onbeforeunload = function () {
    return message;
  };
}

export function allowExit() {
  window.onbeforeunload = null;
}

export function getConfigUploader(jwt = "") {
  return {
    url: `${baseURL}/api/upload`,
    prepareData: (formData) => {
      const files = Object.values(
        Object.fromEntries(
          Array.from(formData.entries()).filter((entry) =>
            entry[0].includes("files"),
          ),
        ),
      );
      const entries = formData.entries();
      for (let entry of entries) {
        formData.delete(entry[0]);
      }
      files.forEach((file) => formData.append("files", file));
      return formData;
    },
    process: (res) => {
      const data = {
        baseurl: baseURL,
        messages: [],
        files: res.map((e) => e.url),
        isImages: res.map((e) => e.mime.includes("image")),
      };
      return data;
    },
    isSuccess: (res) => Array.isArray(res) && res.length > 0,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };
}

export function isInIframe() {
  try {
    return window !== window.parent;
  } catch (error) {
    return Boolean(error);
  }
}

export function postToParent(message) {
  const parentWindow = window.parent;
  if (parentWindow) {
    parentWindow.postMessage(message, "*");
  }
}

export function reconstructBookingRequest(bookings) {
  if (!bookings || bookings.length === 0) {
    return null;
  }

  const baseBooking = bookings[0];

  const reconstructedObject = {
    id: baseBooking.id,
    firstName: baseBooking.firstName,
    lastName: baseBooking.lastName,
    email: baseBooking.email,
    phone: baseBooking.phone,
    passengers: baseBooking.passengers,
    adults: baseBooking.adults ?? null,
    childrenUnder4: baseBooking.childrenUnder4 ?? null,
    childrenOver4: baseBooking.childrenOver4 ?? null,
    luggage: baseBooking.luggage,
    transportType: baseBooking.transportType,
    destinationType: baseBooking.destinationType,
    address: baseBooking.address,
    hotel: baseBooking.hotel,
    zone: baseBooking.zone,
    originCity: baseBooking.city,
    hasAdditionalLuggage: baseBooking.hasAdditionalLuggage,
    additionalComments: baseBooking.additionalComments,
    createdAt: baseBooking.createdAt,
    coupon: baseBooking.coupon || null,
    discountAmount: baseBooking.discountAmount || null,
    payment: baseBooking.payment,
    status: baseBooking.status,
    paymentMethod: baseBooking.paymentMethod,
  };

  if (bookings.length === 2) {
    reconstructedObject.tripType = "both";

    const departBooking = bookings.find(
      (booking) => booking.tripType === "depart",
    );
    const returnBooking = bookings.find(
      (booking) => booking.tripType === "return",
    );

    if (!departBooking || !returnBooking) {
      return null;
    }

    reconstructedObject.arrivalDate = departBooking.date;
    reconstructedObject.arrivalTime = String(departBooking.flightTime);
    reconstructedObject.arrivalSchedule = departBooking.time;
    reconstructedObject.flightNumber = departBooking.flightNumber;

    reconstructedObject.returnDate = returnBooking.date;
    reconstructedObject.returnTime = String(returnBooking.flightTime);
    reconstructedObject.returnSchedule = returnBooking.time;
    reconstructedObject.returnFlightNumber = returnBooking.flightNumber;
  } else {
    if (baseBooking.tripType === "depart") {
      reconstructedObject.tripType = "oneWay";
      reconstructedObject.arrivalDate = baseBooking.date;
      reconstructedObject.arrivalTime = String(baseBooking.flightTime);
      reconstructedObject.arrivalSchedule = baseBooking.time;
      reconstructedObject.flightNumber = baseBooking.flightNumber;
    } else if (baseBooking.tripType === "return") {
      reconstructedObject.tripType = "return";
      reconstructedObject.returnDate = baseBooking.date;
      reconstructedObject.returnTime = String(baseBooking.flightTime);
      reconstructedObject.returnSchedule = baseBooking.time;
      reconstructedObject.returnFlightNumber = baseBooking.flightNumber;
    }
  }

  return reconstructedObject;
}
